import { useEffect, useRef, useState } from "react";
import { getCmTableGeo, getCmTableBulkGeo } from "../services/cmTables";
import { ApiEndpoint, ExtendedCircleLayer, MapLibreLayerSpecification } from "../types";
import { getBboxByGeom, isBboxContained } from "../utilities/bboxUtils";
import { useMapActions, useMapStore } from "../store/map";
import { useLayerActions, useLayerSelector } from "../store/layer";

/**
 * useCmTableMapLayers
 *
 * Two-track strategy:
 *
 * Track A – Selected table:
 *   Fetches all features once (no bbox) to allow extent calculation and flying.
 *
 * Track B – Background tables:
 *   Fetches features within viewport via bulk-geojson API.
 *   Optimization: Only refetches if the map moves OUTSIDE the previously fetched area.
 *   Zooming in (smaller bbox) does NOT trigger a refetch if the area is already covered.
 */

/**
 * A CM-table point style is a "symbol" style when it carries icon/label config
 * (the same shape as legacy geojson `style` blocks) rather than plain circle paint.
 * These come from the endpoint's `map_display_config.style`.
 */
const isSymbolStyle = (style?: Record<string, any>): boolean =>
  !!style && ("iconStyle" in style || "defaultIcon" in style || "labelField" in style);

/**
 * Translates a legacy `style` block (iconStyle/iconSize/label*) into MapLibre
 * symbol `layout` + `paint`, matching the symbol branch of transformToMapLibreLayer
 * so CM-table point layers render icons and labels like classic geojson layers.
 */
function buildSymbolLayoutPaint(style: Record<string, any>): { layout: any; paint: any } {
  const iconImage = style.iconStyle || style.defaultIcon;

  const layout: any = {
    visibility: "visible",
    "icon-allow-overlap": true,
    "icon-ignore-placement": true,
    "symbol-placement": "point",
    "symbol-z-order": "source",
    "icon-optional": false,
    "text-optional": true,
    "symbol-sort-key": 1
  };
  const paint: any = {};

  if (iconImage) {
    layout["icon-image"] = iconImage;
    layout["icon-size"] = style.iconSize ?? 0.3;
  }

  if (style.labelField) {
    layout["text-field"] = style.labelField;
    layout["text-size"] = style.labelSize ?? 12;
    layout["text-font"] = style.labelFont ?? ["Mukta, sans-serif"];
    layout["text-offset"] = style.labelOffset ?? [1, 0];
    paint["text-color"] = style.labelColor ?? "#ffffff";

    if (style.labelOpacity !== undefined) paint["text-opacity"] = style.labelOpacity;
    if (style.labelHaloColor !== undefined) {
      paint["text-halo-color"] = style.labelHaloColor;
      paint["text-halo-width"] = style.labelHaloWidth ?? 1;
    }
  }

  return { layout, paint };
}

/**
 * Sub-layer id suffixes used for mixed-geometry ("geometry") CM tables.
 *
 * A MapLibre layer renders exactly one geometry family (circle→points,
 * line→lines, fill→polygons), so a table whose rows can each be a point, line
 * or polygon is drawn with three sibling layers over ONE shared GeoJSON source,
 * each scoped by a `$type` filter. The point layer keeps the canonical
 * `cm-table-<slug>` id; the line/fill siblings append these suffixes.
 */
const CM_SUBLAYER_SUFFIX = { line: "::line", fill: "::fill" } as const;

/** Strip a geometry sub-layer suffix, returning the canonical `cm-table-<slug>` base id. */
export const stripCmSubLayerSuffix = (layerId: string): string => layerId.replace(/::(?:line|fill)$/, "");

/** True when `layerId` (base or sub-layer) belongs to the table keyed by `baseId`. */
const belongsToBaseLayer = (layerId: string, baseId: string): boolean => stripCmSubLayerSuffix(layerId) === baseId;

// `$type` normalises Multi* geometries to their base family, so these filters
// also catch MultiPoint / MultiLineString / MultiPolygon features.
const POINT_FILTER = ["==", "$type", "Point"];
const LINE_FILTER = ["==", "$type", "LineString"];
const POLYGON_FILTER = ["==", "$type", "Polygon"];

function buildLayerSpec(
  layerId: string,
  style: Record<string, any> | undefined,
  spatialType: "point" | "multipoint" | "polygon" | "multipolygon" | "linestring" | "multilinestring" | "none",
  geojson: any,
  displayName?: string,
  filter?: any[]
): MapLibreLayerSpecification {
  const source = { id: layerId, type: "geojson" as const, data: geojson };
  const DEFAULT_COLOR = "#3b82f6";
  const filterSpread = filter ? { filter } : {};

  // Normalise multi-types to their base render type
  const effectiveType =
    spatialType === "multipolygon"
      ? "polygon"
      : spatialType === "multilinestring"
        ? "linestring"
        : spatialType === "multipoint"
          ? "point"
          : spatialType;

  if (effectiveType === "point") {
    // Icon/label styling → render as a symbol layer (like legacy "Cultural Sites").
    if (isSymbolStyle(style)) {
      const { layout, paint } = buildSymbolLayoutPaint(style!);
      return {
        id: layerId,
        type: "symbol",
        source,
        layout,
        paint,
        ...filterSpread,
        initialVisibility: true,
        disableClicking: false,
        _spatialType: spatialType,
        displayName
      } as any;
    }

    const color = style?.["circle-color"] ?? DEFAULT_COLOR;
    return {
      id: layerId,
      type: "circle",
      source,
      layout: { visibility: "visible" },
      paint: {
        "circle-radius": 7,
        "circle-color": color,
        "circle-stroke-width": 1.5,
        "circle-stroke-color": "#ffffff",
        ...style
      },
      ...filterSpread,
      initialVisibility: true,
      disableClicking: false,
      _spatialType: spatialType,
      displayName
    } as any;
  }

  if (effectiveType === "linestring") {
    const color = style?.["line-color"] ?? DEFAULT_COLOR;
    return {
      id: layerId,
      type: "line",
      source,
      layout: { visibility: "visible", "line-join": "round", "line-cap": "round" },
      paint: { "line-color": color, "line-width": 3, ...style },
      ...filterSpread,
      initialVisibility: true,
      disableClicking: false,
      _spatialType: spatialType,
      displayName
    } as any;
  }

  // polygon / none — default to fill
  const color = style?.["fill-color"] ?? DEFAULT_COLOR;
  return {
    id: layerId,
    type: "fill",
    source,
    layout: { visibility: "visible" },
    paint: { "fill-color": color, "fill-opacity": 0.4, "fill-outline-color": color, ...style },
    ...filterSpread,
    initialVisibility: true,
    disableClicking: false,
    _spatialType: spatialType,
    displayName
  } as any;
}

/**
 * Build the MapLibre layer spec(s) for a single CM-table endpoint.
 *
 * Single-geometry tables (point / linestring / polygon) yield one layer.
 * Generic "geometry" tables yield three `$type`-filtered siblings sharing one
 * source so each row renders in whichever family (point / line / polygon) its
 * geometry actually is — the geometry `$type` is authoritative, so no per-row
 * `spatial_type` bookkeeping is required.
 */
function buildLayerSpecs(
  baseId: string,
  style: Record<string, any> | undefined,
  spatialType: string,
  geojson: any,
  displayName?: string
): MapLibreLayerSpecification[] {
  if (spatialType !== "geometry") {
    return [buildLayerSpec(baseId, style, spatialType as any, geojson, displayName)];
  }

  const pointSpec = buildLayerSpec(baseId, style, "point", geojson, displayName, POINT_FILTER);
  const lineSpec = buildLayerSpec(`${baseId}${CM_SUBLAYER_SUFFIX.line}`, style, "linestring", geojson, displayName, LINE_FILTER);
  const fillSpec = buildLayerSpec(`${baseId}${CM_SUBLAYER_SUFFIX.fill}`, style, "polygon", geojson, displayName, POLYGON_FILTER);

  // The base (point) layer represents the table in the Layers panel; flag it as
  // "geometry" and mark the siblings so the panel renders one row per table.
  (pointSpec as any)._spatialType = "geometry";
  (lineSpec as any)._cmSubLayer = true;
  (fillSpec as any)._cmSubLayer = true;

  return [pointSpec, lineSpec, fillSpec];
}

const EMPTY_FC = { type: "FeatureCollection", features: [] };

interface UseCmTableMapLayersOptions {
  cmTableEndpoints: ApiEndpoint[];
  activeTableSlug: string | null;
  bbox: string | null;
  showOnlyFilter: boolean;
}

export interface UseCmTableMapLayersResult {
  layers: MapLibreLayerSpecification[];
  selectedTableExtent: number[] | null;
}

export const useCmTableMapLayers = ({
  cmTableEndpoints,
  activeTableSlug,
  bbox,
  showOnlyFilter
}: UseCmTableMapLayersOptions): UseCmTableMapLayersResult => {
  const [layers, setLayers] = useState<MapLibreLayerSpecification[]>([]);
  const [selectedTableExtent, setSelectedTableExtent] = useState<number[] | null>(null);
  const { setIsCmTableLoading } = useMapActions();
  const cmTableLayerVersion = useMapStore((state) => state.cmTableLayerVersion);
  const { setLayerVisibility } = useLayerActions();
  const { visibleLayers } = useLayerSelector();

  const activeTableLoadedRef = useRef<string | null>(null);
  // Track the last cmTableLayerVersion we've acted on to detect bumps
  const lastLayerVersionRef = useRef<number>(cmTableLayerVersion);

  // Track which dynamic tables have been explicitly unchecked/hidden by the user
  const explicitlyHiddenRef = useRef<Set<string>>(new Set());

  // Track the most recently fetched bbox for background tables to avoid redundant fetches on zoom-in.
  const lastBulkFetchRef = useRef<{ slugs: string; bbox: number[] } | null>(null);

  const spatialEndpoints = cmTableEndpoints.filter((ep) => ep.spatialType && ep.spatialType !== "none");
  const tableSlugKey = spatialEndpoints.map((ep) => ep.tableSlug || ep.tableName).join(",");

  // 1. Track explicit user hides so effect 2 can restore visibility when the active
  //    endpoint changes back to a previously-hidden layer.  Auto-initialization is
  //    intentionally NOT done here — layers start hidden and are only enabled by
  //    effect 2 (activeTableSlug change) or explicit user selection from the drawer.
  useEffect(() => {
    spatialEndpoints.forEach((ep) => {
      const layerId = `cm-table-${ep.tableSlug || ep.tableName}`;
      if (visibleLayers.has(layerId)) {
        explicitlyHiddenRef.current.delete(layerId);
      } else {
        explicitlyHiddenRef.current.add(layerId);
      }
    });
  }, [visibleLayers, tableSlugKey, spatialEndpoints]);

  // 2. Auto-enable active CM Table layer when selected in the sidebar (Your suggestion!)
  useEffect(() => {
    if (activeTableSlug) {
      const activeLayerId = `cm-table-${activeTableSlug}`;
      explicitlyHiddenRef.current.delete(activeLayerId);
      setLayerVisibility(activeLayerId, true);
    }
  }, [activeTableSlug, setLayerVisibility]);

  // 3. Build placeholder specs
  useEffect(() => {
    if (spatialEndpoints.length === 0) {
      activeTableLoadedRef.current = null;
      setLayers([]);
      return;
    }

    const nextLayerIds = new Set(spatialEndpoints.map((ep) => `cm-table-${ep.tableSlug || ep.tableName}`));

    // If the active table's layer ID is no longer present, reset the loaded ref
    if (activeTableLoadedRef.current && !nextLayerIds.has(`cm-table-${activeTableLoadedRef.current}`)) {
      activeTableLoadedRef.current = null;
    }

    setLayers((prev) => {
      const previousLayersById = new Map(prev.map((layer: any) => [layer.id, layer]));

      return spatialEndpoints.flatMap((ep) => {
        const baseId = `cm-table-${ep.tableSlug || ep.tableName}`;
        const specs = buildLayerSpecs(baseId, ep.style, ep.spatialType!, EMPTY_FC, ep.name);

        // Preserve existing specs (they hold already-fetched GeoJSON) when present.
        return specs.map((spec) => previousLayersById.get(spec.id) ?? spec);
      });
    });
  }, [tableSlugKey]);

  // 2. Selected table full fetch (Track A)
  useEffect(() => {
    if (!activeTableSlug) {
      setSelectedTableExtent(null);
      activeTableLoadedRef.current = null;
      return;
    }

    const activeEndpoint = spatialEndpoints.find((ep) => (ep.tableSlug || ep.tableName) === activeTableSlug);
    if (!activeEndpoint) return;

    // A version bump (after row edit) resets the guard so we re-fetch fresh GeoJSON
    if (cmTableLayerVersion !== lastLayerVersionRef.current) {
      lastLayerVersionRef.current = cmTableLayerVersion;
      activeTableLoadedRef.current = null;
    }

    if (activeTableLoadedRef.current === activeTableSlug) return;

    let cancelled = false;

    setIsCmTableLoading(true);
    getCmTableGeo(activeEndpoint.tableSlug || activeEndpoint.tableName)
      .then((res) => {
        if (cancelled) return;

        const layerId = `cm-table-${activeEndpoint.tableSlug || activeEndpoint.tableName}`;
        const geojson = res.data;

        if (geojson.features.length > 0) {
          const extent = getBboxByGeom(geojson);
          if (extent) {
            setSelectedTableExtent(extent as number[]);
          }
        }

        activeTableLoadedRef.current = activeTableSlug;

        setLayers((prev) =>
          prev.map((l: any) =>
            belongsToBaseLayer(l.id, layerId) && typeof l.source !== "string" ? { ...l, source: { ...l.source, data: geojson } } : l
          )
        );
      })
      .catch((err) => console.warn(`[useCmTableMapLayers] Active table fetch failed:`, err))
      .finally(() => {
        if (!cancelled) setIsCmTableLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTableSlug, tableSlugKey, cmTableLayerVersion, setIsCmTableLoading]);

  // 3. Background tables bulk fetch (Track B) with containment optimization
  useEffect(() => {
    if (!bbox || showOnlyFilter || activeTableLoadedRef.current === null) return;

    // Only include background tables that are currently toggled ON in the layer panel
    const backgroundEndpoints = spatialEndpoints.filter((ep) => {
      const layerId = `cm-table-${ep.tableSlug || ep.tableName}`;
      return (ep.tableSlug || ep.tableName) !== activeTableSlug && visibleLayers.has(layerId);
    });
    if (backgroundEndpoints.length === 0) return;

    const backgroundSlugs = backgroundEndpoints.map((ep) => ep.tableSlug || ep.tableName).sort();
    const slugsStr = backgroundSlugs.join(",");

    // Parse current bbox string to numbers for comparison
    const currentBbox = bbox.split(",").map(Number);
    if (currentBbox.length !== 4) return;

    // Optimization: Check if current view is already fully covered by the last fetch
    if (lastBulkFetchRef.current && lastBulkFetchRef.current.slugs === slugsStr) {
      if (isBboxContained(lastBulkFetchRef.current.bbox, currentBbox)) {
        return;
      }
    }

    let cancelled = false;

    setIsCmTableLoading(true);
    getCmTableBulkGeo(backgroundSlugs, bbox)
      .then((res) => {
        if (cancelled) return;

        // Update the "last fetched" tracking
        lastBulkFetchRef.current = { slugs: slugsStr, bbox: currentBbox };

        const perTable: Record<string, any[]> = {};
        backgroundSlugs.forEach((slug) => (perTable[slug] = []));

        res.data.features.forEach((f: any) => {
          // Use table_slug for grouping
          const slug = f.properties?.table_slug;
          if (slug && perTable[slug]) perTable[slug].push(f);
        });

        setLayers((prev) =>
          prev.map((l: any) => {
            const endpoint = backgroundEndpoints.find((ep) => `cm-table-${ep.tableSlug || ep.tableName}` === stripCmSubLayerSuffix(l.id));
            if (!endpoint || typeof l.source === "string") return l;
            return {
              ...l,
              source: { ...l.source, data: { type: "FeatureCollection", features: perTable[endpoint.tableSlug || endpoint.tableName] } }
            };
          })
        );
      })
      .catch((err) => console.warn("[useCmTableMapLayers] Bulk fetch failed:", err))
      .finally(() => {
        if (!cancelled) setIsCmTableLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [bbox, showOnlyFilter, activeTableSlug, tableSlugKey, visibleLayers, setIsCmTableLoading]);

  return { layers, selectedTableExtent };
};
